export type CDNOptionsType =
  | 'cdn1'
  | 'cdn3'
  | 'cdn4'
  | 'cdn5'
  | 'cdn6'
  | 'cdn7'
  // For unit test purposes
  | 'cdn-test1'
  | 'cdn-test2'
  | 'cdn-test3'
  | 'cdn-test4'

/**
 * CDN option that is either a known subdomain literal (CDNOptionsType) or an
 * arbitrary full hostname supplied via the `cdn_host` param (e.g.
 * "cdn-a.example.com"). Full hostnames are detected by the presence of a dot.
 */
export type CDNOption = CDNOptionsType | (string & NonNullable<unknown>)

// Do not modify `.fireworkn.com` as it points to Alibaba CDN for China users.
const IGNORE_REGEXP = /\.fireworkn\.com/

// Replace cdn prefix with a random CDN subdomain.
const CDN_SEARCH_REGEXP = /\/\/cdn\d+\./

// Matches the full host (including the existing cdn subdomain and domain) so
// it can be replaced wholesale when a full hostname override is provided.
const CDN_HOST_REGEXP = /\/\/cdn\d+\.[^/?#]+/

// CDN subdomains to replace the cdn prefix with.
const CDN_OPTIONS: CDNOptionsType[] = [
  'cdn1',
  'cdn3',
  'cdn4',
  'cdn5',
  'cdn6',
  'cdn7',
]

/**
 * Replace cdn prefix with a random CDN subdomain. Note: Hashing the url
 * ensures that the same url will always receive the same subdomain, otherwise
 * browsers will cancel unfinished requests.
 *
 * When an option contains a dot it is treated as a full hostname override
 * (e.g. "cdn-a.example.com") and the entire host portion is replaced.
 * Protocol prefixes ("https://...") in the option value are stripped
 * automatically.
 *
 * @param url
 * @returns
 */
export function roundRobinCDN(url: string, _optionsDI?: CDNOption[]) {
  if (url && !IGNORE_REGEXP.test(url)) {
    // Simple hash function to consistently select a CDN
    let hash = 0
    for (let i = 0; i < url.length; i++) {
      const char = url.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash |= 0 // Convert to 32bit integer
    }
    const options =
      _optionsDI && _optionsDI.length > 0 ? _optionsDI : CDN_OPTIONS
    const index = Math.abs(hash) % options.length
    const raw = options[index]
    // A protocol prefix ("https://") or protocol-relative prefix ("//") signals
    // an explicit full-host override. Strip it up front so it can never leak
    // into the rewritten url (avoids "https://https" or "////").
    const hasHostPrefix = /^(https?:)?\/\//.test(raw)
    const chosen = raw.replace(/^(https?:)?\/\//, '')

    // Full-host mode: option has a protocol/protocol-relative prefix or contains
    // a dot → replace the entire host segment (e.g. "https://cdn-override",
    // "//cdn-override" or "cdn-a.example.com").
    if (hasHostPrefix || chosen.includes('.')) {
      return url.replace(CDN_HOST_REGEXP, `//${chosen}`)
    }

    return url.replace(CDN_SEARCH_REGEXP, `//${chosen}.`)
  }

  return url
}
